/**
 * Algopix Job Monitor — admin-only Type 1 Standard surface at /admin/algopix-jobs.
 *
 * Day 99b.1 — first of the Day-99b arbi-dev ops-surface cluster; replaces the
 * Day-62a Placeholder. Structurally mirrors FeedbackInbox.tsx (themeQuartz grid,
 * AG Grid Infinite Row Model, FilterGroup pills, search, slide-in detail panel,
 * useDocumentTitle) and reuses arbi-dev primitives (ConfirmDialog,
 * reportDatasourceFailure) per ADR 36. Diverges on:
 *   - Datasource — PostgREST-direct on ingestion_batches (status / processed_count /
 *     row_count / error_log), { count: 'exact' } + .range(...); no read RPC
 *     (bounded volume, FeedbackInbox precedent).
 *   - Read-only queue — imports aren't a triage queue, so NO bulk-action bar and
 *     NO status-mutating RPC. The only action is "Re-import", a cross-subdomain
 *     redirect to the arbi-root Files import flow: the original file is never
 *     persisted (ingestion_batches.storage_path is NULL for client-side
 *     useAlgopixImport rows), so there is no server-side re-run — recovery is a
 *     re-upload in Arbi. Mirrors the Sidebar.tsx ARBI_DASHBOARD_URL cross-subdomain
 *     precedent; ConfirmDialog guards the leave-arbi-dev navigation.
 *   - Detail panel enriches with the linked files row via a direct
 *     files.eq('related_import_id', id) read (NOT get_files_by_status, which is
 *     lifecycle-status-keyed — not import-keyed).
 *   - Distributor + manufacturer names resolved via mount-time id→name maps (the
 *     Settings loadActors pattern) — no PostgREST embed. (W4b-β2: the §95-b
 *     supplier_id→manufacturer_id swap — ingestion_batches.manufacturer_id, written by
 *     useAlgopixImport since W1; legacy rows with a NULL manufacturer_id show the em-dash.)
 *
 * NO nav badge in 99b.1 (deferred): the useful signal (processing OR failed) is
 * non-eq and useBadgeCount is eq-only, and ingestion_batches has no ack/clear
 * mechanism so a failed-count badge would never zero. See BACKLOG.
 */

import { useState, useCallback, useRef, useMemo, useEffect, type JSX } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
  type ColDef,
  type GridApi,
  type GridReadyEvent,
  type ICellRendererParams,
  type IGetRowsParams,
  type RowClickedEvent,
  type ValueFormatterParams,
} from 'ag-grid-community';
import { Search, X, AlertTriangle, Upload, ExternalLink } from 'lucide-react';
import clsx from 'clsx';
import { supabase } from '../lib/supabase';
import { reportDatasourceFailure } from '../lib/agGridErrorTracker';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

ModuleRegistry.registerModules([AllCommunityModule]);

// The arbi-root Files import flow lives cross-subdomain (re-upload happens
// there; the original Algopix file is never persisted, so re-import = re-upload).
const ARBI_FILES_URL = 'https://arbi.ironcladsupply.co/files';

// ── Local types ───────────────────────────────────────────────────────────────

type AlgopixStatus = 'uploaded' | 'processing' | 'complete' | 'failed';

interface AlgopixImportRow {
  id: string;
  file_name: string;
  storage_path: string | null;
  row_count: number | null;
  processed_count: number | null;
  status: AlgopixStatus | string;
  error_log: unknown;
  distributor_id: string | null;
  manufacturer_id: string | null;
  uploaded_by: string | null;
  imported_at: string;
  completed_at: string | null;
  created_at: string;
}

interface LinkedFile {
  display_name: string | null;
  lifecycle_status: string;
  storage_path: string | null;
  file_type: string | null;
  uploaded_at: string;
}

interface ImportErrorEntry {
  row?: number | string;
  friendly?: string;
  technical?: string;
}

interface ToastState {
  type: 'success' | 'error';
  message: string;
}

// ── Filter constants ──────────────────────────────────────────────────────────

const STATUS_FILTERS: ReadonlyArray<{ id: AlgopixStatus | null; label: string }> = [
  { id: null, label: 'All' },
  { id: 'uploaded', label: 'Uploaded' },
  { id: 'processing', label: 'Processing' },
  { id: 'complete', label: 'Complete' },
  { id: 'failed', label: 'Failed' },
] as const;

// ── Status badge ────────────────────────────────────────────────────────────--

function StatusBadge({ status }: { status: string }): JSX.Element {
  const cls: string =
    (
      {
        uploaded: 'bg-info/15 text-info',
        processing: 'bg-warning/15 text-warning',
        complete: 'bg-success/15 text-success',
        failed: 'bg-danger/15 text-danger',
      } as Record<string, string>
    )[status] || 'bg-surface-3 text-ink-muted';
  const label: string =
    (
      {
        uploaded: 'Uploaded',
        processing: 'Processing',
        complete: 'Complete',
        failed: 'Failed',
      } as Record<string, string>
    )[status] || status;
  return (
    <span className={clsx('px-1.5 py-0.5 rounded text-2xs font-medium whitespace-nowrap', cls)}>
      {label}
    </span>
  );
}

// ── Relative time (inline-replicated per FeedbackInbox/ErrorLogViewer precedent) ─

function relativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function progressLabel(processed: number | null, total: number | null): string {
  const p = processed ?? 0;
  if (total == null) return `${p.toLocaleString()} / —`;
  return `${p.toLocaleString()} / ${total.toLocaleString()}`;
}

// ── Filter pill component (inline-replicated) ───────────────────────────────────

interface FilterGroupProps<T extends string | null> {
  options: ReadonlyArray<{ id: T; label: string }>;
  value: T;
  onChange: (id: T) => void;
}

function FilterGroup<T extends string | null>({
  options,
  value,
  onChange,
}: FilterGroupProps<T>): JSX.Element {
  return (
    <div className="flex items-center bg-surface-2 rounded-md p-0.5 gap-0.5 border border-border-subtle">
      {options.map((f) => (
        <button
          key={f.id ?? 'all'}
          onClick={() => onChange(f.id)}
          aria-pressed={value === f.id}
          className={clsx(
            'px-2 py-0.5 text-2xs font-medium rounded transition-colors cursor-pointer whitespace-nowrap',
            value === f.id ? 'bg-surface-4 text-ink-bright' : 'text-ink-muted hover:text-ink-secondary'
          )}
        >
          {f.label}
        </button>
      ))}
    </div>
  );
}

// ── Toast (inline-replicated) ───────────────────────────────────────────────────

function Toast({
  toast,
  onDismiss,
}: {
  toast: ToastState | null;
  onDismiss: () => void;
}): JSX.Element | null {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [toast, onDismiss]);
  if (!toast) return null;
  const bg =
    toast.type === 'success'
      ? 'bg-success/15 border-success/30 text-success'
      : 'bg-danger/15 border-danger/30 text-danger';
  return (
    <div
      className={clsx(
        'fixed top-4 right-4 z-50 px-4 py-2.5 rounded-lg border text-sm font-medium shadow-lg',
        bg
      )}
    >
      {toast.message}
    </div>
  );
}

// ── Error-log renderer ──────────────────────────────────────────────────────────

function ErrorLogList({ errorLog }: { errorLog: unknown }): JSX.Element {
  if (!errorLog) return <p className="text-xs text-ink-muted">No error detail recorded.</p>;
  const entries: unknown[] = Array.isArray(errorLog) ? errorLog : [errorLog];
  return (
    <ul className="space-y-1.5">
      {entries.slice(0, 50).map((e, i) => {
        const entry = (typeof e === 'object' && e !== null ? e : {}) as ImportErrorEntry;
        const friendly =
          typeof entry.friendly === 'string' ? entry.friendly : JSON.stringify(e);
        const rowLabel = entry.row != null ? `Row ${entry.row}: ` : '';
        return (
          <li key={i} className="text-xs text-danger break-words">
            {rowLabel}
            {friendly}
          </li>
        );
      })}
    </ul>
  );
}

// ── Detail panel ────────────────────────────────────────────────────────────────

interface DetailPanelProps {
  row: AlgopixImportRow;
  distName: string;
  manuName: string;
  onClose: () => void;
  onReimport: () => void;
}

function AlgopixDetailPanel({
  row,
  distName,
  manuName,
  onClose,
  onReimport,
}: DetailPanelProps): JSX.Element {
  // undefined = loading, null = no linked file, LinkedFile = found
  const [linkedFile, setLinkedFile] = useState<LinkedFile | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    setLinkedFile(undefined);
    (async () => {
      const { data } = await supabase
        .from('files')
        .select('display_name, lifecycle_status, storage_path, file_type, uploaded_at')
        .eq('related_import_id', row.id)
        .limit(1);
      if (cancelled) return;
      setLinkedFile(data && data.length > 0 ? (data[0] as LinkedFile) : null);
    })();
    return () => {
      cancelled = true;
    };
  }, [row.id]);

  const isFailed = row.status === 'failed';

  return (
    <div className="w-80 flex-shrink-0 bg-surface-1 border-l border-border h-full overflow-y-auto pr-2.5">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
        <h3 className="text-sm font-semibold text-ink-bright truncate pr-2">{row.file_name}</h3>
        <button
          onClick={onClose}
          className="text-ink-muted hover:text-ink-primary transition-colors cursor-pointer"
          aria-label="Close panel"
        >
          <X size={16} />
        </button>
      </div>
      <div className="p-4 space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <StatusBadge status={row.status} />
          <span className="text-2xs font-mono text-ink-muted">#{row.id.slice(-6)}</span>
        </div>

        <div>
          <p className="text-2xs uppercase tracking-wider text-ink-muted mb-1">Progress</p>
          <p className="text-xs font-mono text-ink-primary">
            {progressLabel(row.processed_count, row.row_count)} rows
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-2xs uppercase tracking-wider text-ink-muted mb-1">Distributor</p>
            <p className="text-xs text-ink-primary break-words">{distName}</p>
          </div>
          <div>
            <p className="text-2xs uppercase tracking-wider text-ink-muted mb-1">Manufacturer</p>
            <p className="text-xs text-ink-primary break-words">{manuName}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-2xs uppercase tracking-wider text-ink-muted mb-1">Imported</p>
            <p className="text-xs font-mono text-ink-muted">{relativeTime(row.imported_at)}</p>
          </div>
          <div>
            <p className="text-2xs uppercase tracking-wider text-ink-muted mb-1">Completed</p>
            <p className="text-xs font-mono text-ink-muted">{relativeTime(row.completed_at)}</p>
          </div>
        </div>

        {/* Source file (linked files row) */}
        <div className="border-t border-border-subtle pt-3">
          <p className="text-2xs uppercase tracking-wider text-ink-muted mb-1">Source file</p>
          {linkedFile === undefined ? (
            <p className="text-xs text-ink-muted">Loading…</p>
          ) : linkedFile === null ? (
            <p className="text-xs text-ink-muted">No linked file record.</p>
          ) : (
            <div className="space-y-0.5">
              <p className="text-xs text-ink-primary break-words">
                {linkedFile.display_name ?? '—'}
              </p>
              <p className="text-2xs font-mono text-ink-muted">
                {linkedFile.lifecycle_status}
                {linkedFile.file_type ? ` · ${linkedFile.file_type}` : ''}
              </p>
            </div>
          )}
          <p className="text-2xs text-ink-muted mt-1.5">
            {row.storage_path
              ? 'Original upload retained.'
              : 'Original file not retained (client-side import) — re-import means re-upload.'}
          </p>
        </div>

        {/* Error log (failed imports) */}
        {isFailed && (
          <div className="border-t border-border-subtle pt-3">
            <p className="text-2xs uppercase tracking-wider text-ink-muted mb-1.5">Errors</p>
            <ErrorLogList errorLog={row.error_log} />
          </div>
        )}

        {/* Re-import affordance — failed-only: re-upload-to-retry is the recovery
            for a failed import. Hidden for complete (done), processing (running —
            re-import would risk a concurrent run), and uploaded (pending, never ran). */}
        {isFailed && (
          <div className="border-t border-border-subtle pt-3">
            <button
              onClick={onReimport}
              className="arbi-btn-ghost text-xs w-full justify-center"
            >
              <Upload size={12} /> Re-import in Arbi
              <ExternalLink size={11} className="opacity-60" />
            </button>
            <p className="text-2xs text-ink-muted mt-1.5 text-center">
              Opens the Files import flow in Arbi to re-upload the source file.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────--

export default function AlgopixJobMonitor(): JSX.Element {
  useDocumentTitle('Algopix Job Monitor');

  const gridRef = useRef<AgGridReact<AlgopixImportRow> | null>(null);
  const gridApiRef = useRef<GridApi<AlgopixImportRow> | null>(null);

  const [searchText, setSearchText] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<AlgopixStatus | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [detailRow, setDetailRow] = useState<AlgopixImportRow | null>(null);
  const [confirmReimport, setConfirmReimport] = useState<boolean>(false);

  // ── Distributor + manufacturer name maps (resolved once on mount) ─────────────
  const [distNames, setDistNames] = useState<Record<string, string>>({});
  const [manuNames, setManuNames] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [{ data: dists }, { data: manus }] = await Promise.all([
        supabase.from('distributors').select('id, name'),
        supabase.from('manufacturers').select('id, name'),
      ]);
      if (cancelled) return;
      if (dists) setDistNames(Object.fromEntries(dists.map((d) => [d.id, d.name])));
      if (manus) setManuNames(Object.fromEntries(manus.map((m) => [m.id, m.name])));
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Resolve id→name from the loaded maps. Depending on the map state means the
  // cell renderers (via columnDefs) and the open detail panel re-render once the
  // maps land — no manual refreshCells needed.
  const resolveDist = useCallback(
    (id: string | null): string => (id ? (distNames[id] ?? `#${id.slice(-6)}`) : '—'),
    [distNames]
  );
  const resolveManu = useCallback(
    // W4b-β2: the §95-b swap — ingestion_batches.manufacturer_id (written by useAlgopixImport
    // since W1). Legacy rows with a NULL manufacturer_id fall back to the em-dash.
    (id: string | null): string => (id ? (manuNames[id] ?? `#${id.slice(-6)}`) : '—'),
    [manuNames]
  );

  const hasFilters = Boolean(statusFilter || searchText.trim());

  // ── State ref for getRowsFn ─────────────────────────────────────
  const stateRef = useRef<{ searchText: string; statusFilter: AlgopixStatus | null }>({
    searchText,
    statusFilter,
  });
  stateRef.current = { searchText, statusFilter };

  // ── Datasource — PostgREST direct ──────────────────────────────
  const getRowsFn = useCallback(async (params: IGetRowsParams): Promise<void> => {
    const { startRow, endRow, successCallback, failCallback } = params;
    const { searchText: st, statusFilter: sf } = stateRef.current;

    try {
      let query = supabase
        .from('ingestion_batches')
        .select('*', { count: 'exact' })
        .order('imported_at', { ascending: false });

      if (sf) query = query.eq('status', sf);
      if (st.trim()) {
        // Escape PostgREST ilike wildcards so a literal % / _ in the box is literal.
        const term = st.trim().replace(/[%_]/g, (m) => `\\${m}`);
        query = query.ilike('file_name', `%${term}%`);
      }

      // IGetRowsParams.endRow is exclusive; PostgREST .range is inclusive.
      const { data, error, count } = await query.range(startRow, endRow - 1);
      if (error) throw new Error(error.message);

      const rows = (data ?? []) as AlgopixImportRow[];
      const total = count ?? 0;
      setTotalCount(total);
      successCallback(rows, total);
    } catch (e) {
      reportDatasourceFailure(
        'AlgopixJobMonitor',
        { rpcName: 'ingestion_batches', params: { startRow, endRow } },
        e,
        () => failCallback()
      );
    }
  }, []);

  const onGridReady = useCallback(
    (params: GridReadyEvent<AlgopixImportRow>): void => {
      gridApiRef.current = params.api;
      params.api.setGridOption('datasource', { getRows: getRowsFn });
    },
    [getRowsFn]
  );

  // Refresh grid when filters change.
  useEffect(() => {
    if (gridApiRef.current) {
      gridApiRef.current.setGridOption('datasource', { getRows: getRowsFn });
    }
  }, [searchText, statusFilter, getRowsFn]);

  // ── Cell renderers ─────────────────────────────────────────────
  const StatusCell = useCallback((p: ICellRendererParams<AlgopixImportRow>): JSX.Element | null => {
    if (!p.data) return null;
    return <StatusBadge status={p.data.status} />;
  }, []);

  const ProgressCell = useCallback(
    (p: ICellRendererParams<AlgopixImportRow>): JSX.Element | null => {
      if (!p.data) return null;
      return (
        <span className="font-mono text-xs text-ink-secondary">
          {progressLabel(p.data.processed_count, p.data.row_count)}
        </span>
      );
    },
    []
  );

  const DistributorCell = useCallback(
    (p: ICellRendererParams<AlgopixImportRow>): JSX.Element | null => {
      if (!p.data) return null;
      return <span className="text-xs text-ink-primary">{resolveDist(p.data.distributor_id)}</span>;
    },
    [resolveDist]
  );

  const CompletedCell = useCallback(
    (p: ICellRendererParams<AlgopixImportRow>): JSX.Element | null => {
      if (!p.data) return null;
      return (
        <span className="font-mono text-xs text-ink-muted">{relativeTime(p.data.completed_at)}</span>
      );
    },
    []
  );

  const ErrorCell = useCallback((p: ICellRendererParams<AlgopixImportRow>): JSX.Element | null => {
    if (!p.data || p.data.status !== 'failed') return null;
    return (
      <span title="Import failed — open for detail">
        <AlertTriangle size={14} className="text-danger" />
      </span>
    );
  }, []);

  // ── Column definitions ─────────────────────────────────────────
  const columnDefs = useMemo<ColDef<AlgopixImportRow>[]>(
    () =>
      [
        {
          colId: 'import_id',
          headerName: 'ID',
          field: 'id',
          suppressMovable: true,
          resizable: false,
          width: 80,
          minWidth: 80,
          pinned: 'left',
          sortable: false,
          cellStyle: { display: 'flex', alignItems: 'center' },
          valueFormatter: (p: ValueFormatterParams<AlgopixImportRow>) =>
            p.value ? '#' + String(p.value).slice(-6) : '—',
          cellClass: 'font-mono text-xs text-ink-muted',
        },
        {
          headerName: 'Status',
          field: 'status',
          width: 110,
          sortable: false,
          cellRenderer: StatusCell,
          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
        {
          headerName: 'File',
          field: 'file_name',
          flex: 1,
          minWidth: 220,
          sortable: false,
          cellStyle: { display: 'flex', alignItems: 'center', color: 'var(--ink-primary)' },
          tooltipField: 'file_name',
        },
        {
          headerName: 'Progress',
          field: 'processed_count',
          width: 130,
          sortable: false,
          cellRenderer: ProgressCell,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'Distributor',
          field: 'distributor_id',
          width: 150,
          sortable: false,
          cellRenderer: DistributorCell,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'Completed',
          field: 'completed_at',
          width: 110,
          sortable: false,
          cellRenderer: CompletedCell,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: '',
          colId: 'error_indicator',
          width: 48,
          maxWidth: 48,
          sortable: false,
          resizable: false,
          cellRenderer: ErrorCell,
          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
      ] as ColDef<AlgopixImportRow>[],
    [StatusCell, ProgressCell, DistributorCell, CompletedCell, ErrorCell]
  );

  const defaultColDef = useMemo<ColDef<AlgopixImportRow>>(
    () => ({
      sortable: false,
      resizable: true,
      headerClass: 'ag-left-aligned-header',
      cellStyle: { textAlign: 'left' },
    }),
    []
  );

  const gridTheme = useMemo(
    () =>
      themeQuartz.withParams({
        backgroundColor: 'var(--surface-2)',
        headerBackgroundColor: 'var(--surface-3)',
        oddRowBackgroundColor: 'var(--surface-2)',
        rowHoverColor: 'var(--surface-3)',
        selectedRowBackgroundColor: 'var(--accent-muted)',
        borderColor: 'var(--border)',
        headerTextColor: 'var(--ink-muted)',
        textColor: 'var(--ink-primary)',
        rowBorder: { color: 'var(--border-subtle)' },
        fontFamily: "'Inter', system-ui, sans-serif",
        fontSize: '12px',
        headerFontSize: '10px',
        headerHeight: 36,
        rowHeight: 42,
        spacing: 4,
      }),
    []
  );

  const onRowClicked = useCallback((event: RowClickedEvent<AlgopixImportRow>): void => {
    if (!event.data) return;
    const domEvent = event.event as Event | undefined;
    const target = domEvent?.target as HTMLElement | null;
    if (target?.closest('button')) return;
    setDetailRow(event.data);
  }, []);

  // ── Re-import (cross-subdomain → arbi-root Files) ──────────────
  const goToFilesImport = useCallback((): void => {
    if (typeof window === 'undefined') return;
    if (import.meta.env.PROD) {
      window.location.assign(ARBI_FILES_URL);
    } else {
      console.warn('[AlgopixJobMonitor] Would navigate to ' + ARBI_FILES_URL + ' (dev mode — skipping)');
      setToast({ type: 'success', message: 'Dev mode — would open Files import in Arbi' });
    }
  }, []);

  return (
    <div className="flex flex-col h-full -m-4">
      <Toast toast={toast} onDismiss={() => setToast(null)} />
      <ConfirmDialog
        open={confirmReimport}
        title="Re-import this file?"
        message="The original file isn't retained, so re-import means re-uploading it. This opens the Files import flow in Arbi — you'll leave arbi-dev."
        confirmLabel="Open Files in Arbi"
        onConfirm={() => {
          setConfirmReimport(false);
          goToFilesImport();
        }}
        onCancel={() => setConfirmReimport(false)}
      />

      {/* Toolbar */}
      <div className="flex-shrink-0 px-4 py-2 bg-surface-1 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <h1 className="text-base font-semibold text-ink-bright">Algopix Job Monitor</h1>
            <span className="text-xs text-ink-muted font-mono">
              {totalCount === 1 ? '1 import' : `${totalCount.toLocaleString()} imports`}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <FilterGroup options={STATUS_FILTERS} value={statusFilter} onChange={setStatusFilter} />
          <div className="relative ml-auto">
            <Search
              size={13}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none"
            />
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search by file name..."
              aria-label="Search imports by file name"
              className="arbi-input pl-8 pr-3 py-1 text-xs w-60"
            />
          </div>
        </div>
      </div>

      {/* Grid + optional detail panel */}
      <div className="flex flex-1" style={{ minHeight: 0 }}>
        <div className="flex-1 p-2" style={{ minHeight: 0 }}>
          <AgGridReact
            ref={gridRef}
            theme={gridTheme}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowModelType="infinite"
            cacheBlockSize={200}
            maxBlocksInCache={50}
            onGridReady={onGridReady}
            onRowClicked={onRowClicked}
            animateRows={false}
            headerHeight={36}
            rowHeight={42}
            suppressCellFocus={true}
            suppressHorizontalScroll={true}
            overlayNoRowsTemplate={
              hasFilters
                ? '<span style="color:var(--ink-muted);font-size:13px">No imports match your filters</span>'
                : '<span style="color:var(--ink-muted);font-size:13px">No Algopix imports yet — they appear here as products are imported in Arbi</span>'
            }
          />
        </div>

        {detailRow && (
          <AlgopixDetailPanel
            row={detailRow}
            distName={resolveDist(detailRow.distributor_id)}
            manuName={resolveManu(detailRow.manufacturer_id)}
            onClose={() => setDetailRow(null)}
            onReimport={() => setConfirmReimport(true)}
          />
        )}
      </div>
    </div>
  );
}
